import React from 'react';
import { ArrowLeft, MapPin, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { Drop } from '../../types';
interface DropDetailScreenProps {
  drop: Drop;
  onBack: () => void;
  onUnlock: () => void;
}
export function DropDetailScreen({
  drop,
  onBack,
  onUnlock
}: DropDetailScreenProps) {
  const getTimeRemaining = () => {
    const end = new Date(drop.endTime);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };
  const getStartTime = () => {
    const start = new Date(drop.startTime);
    return start.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  return <div className="min-h-screen bg-gray-50 pb-24" data-id="element-526">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-5 py-6 sticky top-0 z-10" data-id="element-527">
        <div className="flex items-center gap-3 mb-4" data-id="element-528">
          <button onClick={onBack} className="w-12 h-12 bg-[#E6FF00] rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform" data-id="element-529">
            <ArrowLeft size={24} className="text-gray-900" strokeWidth={2.5} data-id="element-530" />
          </button>
          <div className="flex-1" data-id="element-531">
            <h1 className="text-2xl font-bold text-white" data-id="element-532">Drop Details</h1>
            {drop.status === 'live' && <div className="flex items-center gap-1.5 mt-1" data-id="element-533">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" data-id="element-534" />
                <span className="text-sm text-purple-100" data-id="element-535">
                  Ends in {getTimeRemaining()}
                </span>
              </div>}
            {drop.status === 'upcoming' && <p className="text-sm text-purple-100 mt-1" data-id="element-536">
                Starts at {getStartTime()}
              </p>}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-5 py-6 space-y-6" data-id="element-537">
        {/* Brand Header */}
        <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-538">
          <div className="flex items-center gap-4 mb-4" data-id="element-539">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden" data-id="element-540">
              <img src={drop.brandLogo} alt={drop.brandName} className="w-full h-full object-cover" data-id="element-541" />
            </div>
            <div className="flex-1" data-id="element-542">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-543">
                {drop.brandName}
              </h2>
              <p className="text-sm text-gray-500" data-id="element-544">{drop.category}</p>
            </div>
            {drop.status === 'live' && <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold" data-id="element-545">
                LIVE
              </div>}
          </div>

          {/* Item Image */}
          <div className="relative aspect-[4/3] bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl overflow-hidden mb-4" data-id="element-546">
            <img src={drop.itemImage} alt={drop.itemName} className="w-full h-full object-cover" data-id="element-547" />
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-548">
            {drop.itemName}
          </h3>
          <div className="flex items-center gap-2 mb-3" data-id="element-549">
            <span className="text-lg font-bold text-purple-600" data-id="element-550">
              Worth ₹{drop.value}
            </span>
            <span className="text-sm text-gray-500" data-id="element-551">• Free with this drop</span>
          </div>
          <p className="text-gray-600 leading-relaxed" data-id="element-552">{drop.description}</p>
        </div>

        {/* How to Redeem */}
        <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-553">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-554">
            <CheckCircle size={20} className="text-purple-600" data-id="element-555" />
            How to Redeem
          </h3>
          <ol className="space-y-3" data-id="element-556">
            <li className="flex gap-3" data-id="element-557">
              <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-bold" data-id="element-558">
                1
              </span>
              <span className="text-gray-700" data-id="element-559">
                Visit any participating store
              </span>
            </li>
            <li className="flex gap-3" data-id="element-560">
              <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-bold" data-id="element-561">
                2
              </span>
              <span className="text-gray-700" data-id="element-562">
                Tap "I'm Here" to unlock your drop
              </span>
            </li>
            <li className="flex gap-3" data-id="element-563">
              <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-bold" data-id="element-564">
                3
              </span>
              <span className="text-gray-700" data-id="element-565">
                Show your drop code to staff
              </span>
            </li>
          </ol>
        </div>

        {/* Participating Stores */}
        <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-566">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-567">
            <MapPin size={20} className="text-purple-600" data-id="element-568" />
            Participating Stores ({drop.participatingStores.length})
          </h3>
          <div className="space-y-3" data-id="element-569">
            {drop.participatingStores.slice(0, 3).map(store => <div key={store.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl" data-id="element-570">
                <div className="flex-1" data-id="element-571">
                  <p className="font-medium text-gray-900" data-id="element-572">{store.name}</p>
                  <p className="text-sm text-gray-500" data-id="element-573">{store.address}</p>
                </div>
                <div className="text-right ml-3" data-id="element-574">
                  <p className="text-sm font-medium text-purple-600" data-id="element-575">
                    {store.distance}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-576">
                    {store.isOpen ? 'Open now' : 'Closed'}
                  </p>
                </div>
              </div>)}
          </div>
          {drop.participatingStores.length > 3 && <button className="w-full mt-3 text-center py-2 text-sm text-purple-600 font-medium hover:text-purple-700 transition-colors" data-id="element-577">
              View All {drop.participatingStores.length} Stores
            </button>}
        </div>

        {/* Terms */}
        <div className="bg-purple-50 border border-purple-200 rounded-2xl p-6" data-id="element-578">
          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-579">
            <AlertCircle size={20} className="text-purple-600" data-id="element-580" />
            Important
          </h3>
          <ul className="space-y-2" data-id="element-581">
            {drop.terms.map((term, index) => <li key={index} className="flex gap-2 text-sm text-gray-700" data-id="element-582">
                <span className="text-purple-600" data-id="element-583">•</span>
                <span data-id="element-584">{term}</span>
              </li>)}
          </ul>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-5 max-w-md mx-auto" data-id="element-585">
        {drop.status === 'live' ? <button onClick={onUnlock} className="w-full bg-[#FFFC00] text-black py-4 rounded-xl font-bold text-lg hover:bg-[#E6E300] transition-all active:scale-[0.98] flex items-center justify-center gap-2" data-id="element-586">
            <MapPin size={20} data-id="element-587" />
            I'm Here - Unlock Drop
          </button> : <button disabled className="w-full bg-gray-200 text-gray-500 py-4 rounded-xl font-bold text-lg cursor-not-allowed flex items-center justify-center gap-2" data-id="element-588">
            <Clock size={20} data-id="element-589" />
            Available {getStartTime()}
          </button>}
      </div>
    </div>;
}